import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { IFetchInvitationsParams, IInvitationsListsSliceInitialState } from './invitationLinksListTypes';
import { createInvitations, getInvitations, getInvitationsInfiniteScroll, reactivateUnusedInvitation } from './invitationLinksListActions';
import { isFulfilledAction, isRejectedAction } from '../sliceHelpers';

const initialState: IInvitationsListsSliceInitialState = {
  invitations: [],
  totalCount: 1,
  filter: {
    skip: 0,
    take: 20,
    showAccepted: false,
    search: '',
  },
  isLoading: false,
  errorMessage: '',
};

export const invitationLinksListSlice = createSlice({
  name: '@@invitationLinksList',
  initialState,
  reducers: {
    setInvitationLinksPageFilter: (
      state,
      action: PayloadAction<IFetchInvitationsParams>
    ) => {
      state.filter = action.payload;
    },
    removeInvitationReducer: (state, action: PayloadAction<number>) => {
      state.invitations = state.invitations.filter((invitation) => invitation.id !== action.payload);
      state.totalCount -= 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getInvitations.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getInvitations.fulfilled, (state, action) => {
      state.invitations = action.payload.list
        .sort((a, b) =>
          (a.member?.id === undefined ? 0 : 1) - (b.member?.id == undefined ? 0 : 1)
          || new Date(b.expirationDate).getTime() - new Date(a.expirationDate).getTime()
        );
      state.totalCount = action.payload.count;
    });
    builder.addCase(getInvitationsInfiniteScroll.fulfilled, (state, action) => {
      const invitations = [...state.invitations, ...action.payload.list]
        .sort((a, b) =>
          (a.member?.id === undefined ? 0 : 1) - (b.member?.id == undefined ? 0 : 1)
          || new Date(b.expirationDate).getTime() - new Date(a.expirationDate).getTime()
        );
      state.invitations = invitations;
      state.totalCount = action.payload.count;
    });
    builder.addCase(createInvitations.fulfilled, (state, action) => {
      state.invitations.unshift(...action.payload);
      state.totalCount += 1;
    });
    builder.addCase(reactivateUnusedInvitation.fulfilled, (state, action) => {
      state.invitations.find((invitation) => invitation.id === action.payload.id)!.expirationDate = action.payload.expirationDate;
    });
    builder.addMatcher(isFulfilledAction, (state) => {
      state.errorMessage = '';
      state.isLoading = false;
    });
    builder.addMatcher(isRejectedAction, (state, action) => {
      state.isLoading = false;
      state.errorMessage = action.payload;
    });
  },
});

export const { setInvitationLinksPageFilter, removeInvitationReducer } = invitationLinksListSlice.actions;

export const isLoadingInvitationsSelector = createSelector(
  [(state: RootState) => state.invitationLinksList.isLoading],
  (isLoading) => isLoading
);
export const listInvitationsSelector = createSelector(
  [(state: RootState) => state.invitationLinksList.invitations],
  (list) => list
);
export const itemsTotalCountInvitationsSelector = createSelector(
  [(state: RootState) => state.invitationLinksList.totalCount],
  (totalCount) => totalCount
);
export const filterInvitationsSelector = createSelector(
  [(state: RootState) => state.invitationLinksList.filter],
  (filter) => filter
);
